/**
 * Hook para buscar logs de ações/movimentações de uma conversa
 *
 * Utilizado para exibir timeline de histórico de movimentações
 * incluindo: criação, mudanças de status, transferências, pausa de bot, etc.
 *
 * RELEASE: v2.0.0 - 2026-05-10 - Migrado para RPC server-side
 *   `get_conversation_action_logs` (single round-trip, dados já enriquecidos
 *   com performer_name/avatar e nomes de agente/departamento destino).
 */

import { useQuery } from '@tanstack/react-query';
import { supabase } from '@/integrations/supabase/client';
import { useAuth } from '@/hooks/useAuth';
import { logger } from '@/lib/logger';

// Tipos de ação suportados
export type ConversationActionType =
  | 'conversation_created'
  | 'status_to_open'
  | 'status_to_closed'
  | 'status_to_spam'
  | 'status_to_pending'
  | 'transfer_agent'
  | 'transfer_department'
  | 'transfer'
  | 'bot_paused'
  | 'bot_resumed'
  | 'agent_assigned';

// Interface do log de ação (shape final entregue ao componente)
export interface ConversationActionLog {
  id: string;
  conversation_id: string;
  action_type: ConversationActionType;
  performed_by: string | null;
  details: Record<string, unknown>;
  created_at: string;
  // Dados enriquecidos (vêm prontos da RPC)
  performer_name?: string | null;
  performer_avatar?: string | null;
  to_agent_name?: string | null;
  to_department_name?: string | null;
}

// Interface de resposta do hook
interface UseConversationActionLogsResult {
  logs: ConversationActionLog[];
  isLoading: boolean;
  error: Error | null;
  refetch: () => void;
}

/**
 * Hook para buscar logs de ações de uma conversa via RPC server-side
 *
 * @param conversationId - ID da conversa
 * @param options - Opções de configuração
 * @returns Logs de ações, estado de loading e erro
 */
export function useConversationActionLogs(
  conversationId: string | null,
  options?: { enabled?: boolean }
): UseConversationActionLogsResult {
  const { enabled = true } = options || {};
  const { currentTenant } = useAuth();
  const tenantId = currentTenant?.id ?? null;

  const query = useQuery({
    queryKey: ['conversation-action-logs', conversationId, tenantId],
    queryFn: async (): Promise<ConversationActionLog[]> => {
      if (!conversationId) return [];

      logger.debug('[useConversationActionLogs] RPC histórico:', conversationId);

      // Single round-trip: RPC já retorna dados enriquecidos
      const { data, error } = await supabase.rpc('get_conversation_action_logs', {
        p_conversation_id: conversationId,
        p_tenant_hint: tenantId,
      });

      if (error) {
        logger.error('[useConversationActionLogs] Erro RPC:', error.message);
        throw error;
      }

      // Mapeia o retorno da RPC para o shape público (sem queries adicionais)
      const logs: ConversationActionLog[] = (data || []).map((row: any) => ({
        id: row.id,
        conversation_id: row.conversation_id,
        action_type: row.action_type as ConversationActionType,
        performed_by: row.performed_by,
        details: (row.details as Record<string, unknown>) || {},
        created_at: row.created_at,
        performer_name: row.performer_name ?? null,
        performer_avatar: row.performer_avatar ?? null,
        to_agent_name: row.to_agent_name ?? null,
        to_department_name: row.to_department_name ?? null,
      }));

      logger.debug('[useConversationActionLogs] Logs:', String(logs.length));
      return logs;
    },
    enabled: enabled && !!conversationId && !!tenantId,
    staleTime: 30000, // 30 segundos
    refetchOnWindowFocus: false,
  });

  return {
    logs: query.data || [],
    isLoading: query.isLoading,
    error: query.error as Error | null,
    refetch: query.refetch,
  };
}
